import { Alert, Button, Checkbox, Group, Menu, Modal, Stack, Text, Textarea, TextInput } from '@mantine/core';
import { useState } from 'react';
import getErrorMessage from '../../../helpers/getErrorMessage';
import useFetch from '../../../helpers/useFetch';
import { applyImport, curlToDraft, importDiff, parseCurl } from './curlImport';
import { templatesFor } from './templates';
import classes from './workspace.module.css';

const MARK_COLOR = { '+': 'success', '-': 'danger', '~': 'warning', '↷': 'warning', '×': 'dimmed' };

const hostOf = (url) => /^https:\/\/(?:[^/?#@]*@)?([^/?#]+)/.exec(url)?.[1] || 'api';

const CurlModal = ({ kind, form, secretsBase, onApply, onClose }) => {
  const fetch = useFetch();
  const [text, setText] = useState('');
  const [literal, setLiteral] = useState([]);
  const [secretName, setSecretName] = useState('');
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState(null);

  let draft = null;
  let parseError = null;
  if (text.trim()) {
    try {
      const declared = form.tool.argRows.map((row) => row.name.trim()).filter(Boolean);
      draft = curlToDraft(parseCurl(text), { kind, literal, declared });
    } catch (err) {
      parseError = err.message;
    }
  }
  const diff = draft ? importDiff(form, draft) : [];
  const name = secretName.trim() || (draft ? `${hostOf(draft.url)} key` : '');

  // The token goes straight into a new secret; the form only ever holds its id.
  const apply = async () => {
    setApplying(true);
    setError(null);
    try {
      let secretId = null;
      if (draft.secret) {
        const res = await fetch(secretsBase, {
          method: 'POST',
          body: JSON.stringify({ name, type: 'static', value: draft.secret.value }),
        });
        secretId = res.secret.id;
      }
      onApply(applyImport(form, draft, secretId));
      onClose();
    } catch (err) {
      setError(getErrorMessage(err, 'The secret could not be created.'));
    } finally {
      setApplying(false);
    }
  };

  return (
    <Modal opened onClose={onClose} title="Paste a cURL" size="lg">
      <Stack gap="xs">
        <Textarea
          aria-label="cURL command"
          autosize
          minRows={4}
          ff="monospace"
          placeholder={"curl 'https://api.example.com/availability?city=Northtown' \\\n  -H 'Authorization: Bearer …'"}
          value={text}
          onChange={(event) => setText(event.target.value)}
        />
        {parseError && (
          <Text size="xs" c="red">
            {parseError}
          </Text>
        )}
        {draft && kind === 'tool' && draft.params.length > 0 && (
          <Group gap="sm">
            <Text size="xs" c="dimmed">
              Query parameters as arguments:
            </Text>
            {draft.params.map((key) => (
              <Checkbox
                key={key}
                size="xs"
                label={key}
                checked={!literal.includes(key)}
                onChange={(event) =>
                  setLiteral((list) =>
                    event.currentTarget.checked ? list.filter((item) => item !== key) : [...list, key]
                  )
                }
              />
            ))}
          </Group>
        )}
        {draft && (
          <Stack gap={0} className={classes.diff} data-testid="curl-diff">
            {diff.length ? (
              diff.map((line, index) => (
                <Text key={index} size="xs" ff="monospace" c={MARK_COLOR[line.mark]}>
                  {line.mark} {line.text}
                </Text>
              ))
            ) : (
              <Text size="xs" c="dimmed">
                Nothing changes.
              </Text>
            )}
          </Stack>
        )}
        {draft?.secret && (
          <TextInput
            label="New secret name"
            description="The token is stored as this secret, write-only, and never kept in the form."
            size="xs"
            value={secretName || name}
            onChange={(event) => setSecretName(event.target.value)}
          />
        )}
        {error && (
          <Alert color="danger" variant="light" py={6}>
            {error}
          </Alert>
        )}
        <Group justify="flex-end" gap="xs">
          <Button size="xs" variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button size="xs" disabled={!draft || !diff.length} loading={applying} onClick={apply}>
            Apply
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

const ExampleModal = ({ onUse, onClose }) => {
  const [text, setText] = useState('');
  const [error, setError] = useState(null);

  const use = () => {
    try {
      onUse(JSON.parse(text));
      onClose();
    } catch {
      setError('That is not valid JSON.');
    }
  };

  return (
    <Modal opened onClose={onClose} title="Paste an example response" size="lg">
      <Stack gap="xs">
        <Text size="xs" c="dimmed">
          Pick rows and fields from it before the API is reachable. Shape only: Set live still needs one real Send.
        </Text>
        <Textarea
          aria-label="Example response"
          autosize
          minRows={6}
          ff="monospace"
          placeholder={'{ "data": { "items": [ { "venue": { "name": "Example Studio" } } ] } }'}
          error={error}
          value={text}
          onChange={(event) => {
            setText(event.target.value);
            setError(null);
          }}
        />
        <Group justify="flex-end" gap="xs">
          <Button size="xs" variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button size="xs" disabled={!text.trim()} onClick={use}>
            Use
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

// Faster starts: a template, a pasted cURL (shown as a diff first) or a pasted example response.
const StartMenu = ({ kind, form, secretsBase, onImport, onTemplate, onExample }) => {
  const [open, setOpen] = useState(null);

  return (
    <>
      <Menu position="bottom-end" withinPortal>
        <Menu.Target>
          <Button size="xs" variant="default">
            Start from ▾
          </Button>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Item onClick={() => setOpen('curl')}>Paste a cURL…</Menu.Item>
          <Menu.Item onClick={() => setOpen('example')}>Paste an example response…</Menu.Item>
          <Menu.Divider />
          <Menu.Label>Templates</Menu.Label>
          {templatesFor(kind).map((template) => (
            <Menu.Item key={template.id} onClick={() => onTemplate(template)}>
              {template.label}
            </Menu.Item>
          ))}
        </Menu.Dropdown>
      </Menu>
      {open === 'curl' && (
        <CurlModal kind={kind} form={form} secretsBase={secretsBase} onApply={onImport} onClose={() => setOpen(null)} />
      )}
      {open === 'example' && <ExampleModal onUse={onExample} onClose={() => setOpen(null)} />}
    </>
  );
};

export default StartMenu;
